import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAppState } from '../../AppStateProvider';
import type { EmailLengthText } from '../../domain/generatedEmail';
import { InsightType } from '../../domain/insightType';
import { USER_SAM_EMAIL_LENGTH_TEXT } from '../../domain/user';
import { useCompanyQuery } from '../../features/company/useCompanyQuery';
import { useCheckIfStaleOpportunity } from '../../features/competitors/useCheckIfStaleOpportunity';
import { insightsQueryKey } from '../../features/insights/insightsQueryKey';
import { useGenerateEmails } from '../../features/insights/useGenerateEmails';
import { useInsightsQuery } from '../../features/insights/useInsightsQuery';
import { useUserSettings } from '../../features/user/useUserSettings';
import { appStore } from '../../services/appStore';
import { useDispatchTrackEventWithLocation } from '../../services/tracking/hooks/useDispatchTrackEventWithLocation';

export const defaultEmailLengthText: EmailLengthText = 'medium';

export const useGenerateEmailsList = () => {
  const { t } = useTranslation();
  const {
    emailGenerationOptions: {
      emailsToGenerate,
      countByInsightId,
      regeneratedOpportunitiesIds,
      updatedOpportunities,
      showToastAfterSuccessfulGeneration,
    },
  } = useAppState();
  const { getUserSetting } = useUserSettings();
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const checkIfStaleOpportunity = useCheckIfStaleOpportunity();
  const queryClient = useQueryClient();
  const { domain: prospect = '' } = useCompanyQuery().data ?? {};
  const samEmailLengthText = getUserSetting(USER_SAM_EMAIL_LENGTH_TEXT) ?? defaultEmailLengthText;

  const [requestedLengthText, setRequestedLengthText] = useState<EmailLengthText>(samEmailLengthText);

  const insightsQuery = useInsightsQuery();

  useEffect(() => {
    if (!insightsQuery.data) {
      return;
    }

    setRequestedLengthText(samEmailLengthText);

    const insightIds = insightsQuery.data.map((i) => i.id);
    const hasAnyNewInsights = insightIds.some((id) => !countByInsightId[id]);

    if (!hasAnyNewInsights) {
      return;
    }

    const newCountByInsightId = insightIds.reduce((acc, insightId) => {
      if (!acc[insightId]) {
        acc[insightId] = 1;
      }

      return acc;
    }, countByInsightId);

    appStore.setCountByInsightId(newCountByInsightId);
  }, [insightsQuery.data, countByInsightId, samEmailLengthText]);

  const generateMoreEmail = () => {
    appStore.incrementEmailsToGenerateCount();
    dispatchTrackEventWithLocation(
      'ai outreach',
      'get more insights',
      `get more talk points/${emailsToGenerate}/${emailsToGenerate}`
    );
  };

  const emailGenerationParams = (insightsQuery.data || [])
    .slice(0, emailsToGenerate)
    .map((i) => ({ insight: i, count: countByInsightId[i.id] ?? 1 }));

  const queriesByInsight = useGenerateEmails(emailGenerationParams, requestedLengthText);
  const firstEmailQuery = queriesByInsight[0]?.queries[0];

  const hasRegenerated = regeneratedOpportunitiesIds.length > 0;

  const isInitialListLoading =
    insightsQuery.isLoading || insightsQuery.isFetching || (firstEmailQuery?.isLoading && !hasRegenerated);

  const isEmptyState = insightsQuery.isError || (insightsQuery.isSuccess && insightsQuery.data?.length === 0);

  useEffect(() => {
    if (showToastAfterSuccessfulGeneration && firstEmailQuery?.isSuccess && !isInitialListLoading && !isEmptyState) {
      appStore.setToast({ content: t('insights.email.regenerate.success') });
      appStore.setShowToastAfterSuccessfulGeneration(false);
    }
  }, [showToastAfterSuccessfulGeneration, firstEmailQuery?.isSuccess, isInitialListLoading, isEmptyState]);

  const isLoading =
    insightsQuery.isLoading ||
    queriesByInsight.some((q) => q.queries[0].isLoading) ||
    (insightsQuery.data || []).length <= emailsToGenerate;

  const regenerateEmail = (insightId: string, lengthParam: EmailLengthText) => {
    if (checkIfStaleOpportunity(insightId)) {
      const previousInsight = insightsQuery.data?.find((i) => i.id === insightId);
      if (!previousInsight || previousInsight.insightType !== InsightType.Opportunity) {
        return;
      }

      const updatedOpportunity = updatedOpportunities.find((o) => o.benchmark.code === previousInsight.benchmark.code);
      const updatedInsights = (insightsQuery.data ?? []).map((insight) => {
        if (insight.id === insightId) {
          return updatedOpportunity ?? insight;
        }

        return insight;
      });
      if (updatedOpportunity) {
        appStore.setRegeneratedOpportunitiesIds([updatedOpportunity.id]);
      }
      queryClient.setQueryData(insightsQueryKey.insights(prospect), updatedInsights);
    } else {
      const count = countByInsightId[insightId] ?? 0;
      appStore.setCountByInsightId({ ...countByInsightId, [insightId]: count + 1 });
      setRequestedLengthText(lengthParam);
    }
  };

  return {
    emailsToGenerate,
    queriesByInsight,
    generateMoreEmail,
    isInitialListLoading,
    isLoading,
    regenerateEmail,
    isEmptyState,
  };
};
